import React, { useEffect, useState } from 'react';
import { investmentAdvisoryApi, FinancialGoalInput, FIIndicatorInput } from '../services/investmentAdvisoryApi.ts';
import { investmentPlanApi } from '../services/investmentPlanApi.ts';
import FinancialIndependenceIndicators from '../components/FinancialIndependenceIndicators.tsx';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import { parseNumberInput, parsePercentInput } from '../utils/numberUtils.ts';
import './InvestmentGoals.css';

interface InvestmentPlan {
  id: number;
  name: string;
}

const InvestmentGoalsPage: React.FC = () => {
  const [plans, setPlans] = useState<InvestmentPlan[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [fiIndicator, setFiIndicator] = useState<any>(null);
  const [goalForm, setGoalForm] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    priority: '1',
    plan_id: undefined as number | undefined,
  });
  const [fiForm, setFiForm] = useState({
    plan_id: 0,
    annual_expenses: '',
    withdrawal_rate: '4',
  });
  const [goalSaving, setGoalSaving] = useState(false);
  const [fiComputing, setFiComputing] = useState(false);
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  useEffect(() => {
    loadPlans();
    loadGoals();
  }, []);

  const loadPlans = async () => {
    const response = await investmentPlanApi.listPlans();
    setPlans(response.data || []);
  };

  const loadGoals = async () => {
    const response = await investmentAdvisoryApi.listGoals();
    setGoals(response.data || []);
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    setGoalSaving(true);
    try {
      const payload: FinancialGoalInput = {
      name: goalForm.name,
      target_amount: parseNumberInput(goalForm.target_amount),
      target_date: goalForm.target_date,
      priority: parseNumberInput(goalForm.priority),
      plan_id: goalForm.plan_id,
    };
      await investmentAdvisoryApi.createGoal(payload);
      await loadGoals();
    } finally {
      setGoalSaving(false);
    }
  };

  const handleComputeFI = async (e: React.FormEvent) => {
    e.preventDefault();
    setFiComputing(true);
    try {
      const payload: FIIndicatorInput = {
      plan_id: fiForm.plan_id,
      annual_expenses: parseNumberInput(fiForm.annual_expenses),
      withdrawal_rate: parsePercentInput(fiForm.withdrawal_rate),
    };
      const response = await investmentAdvisoryApi.computeFIIndicators(payload);
      setFiIndicator(response.data);
    } finally {
      setFiComputing(false);
    }
  };

  return (
    <div className="investment-goals module-name">
      <div className="page-header">
        <div>
          <h1>Metas e Independência Financeira</h1>
          <p>Defina metas e acompanhe indicadores de independência.</p>
        </div>
      </div>

      <div className="module-content">
        {!backendAvailable && (
          <div className="message error" style={{ marginBottom: '16px' }}>
            ⚠️ Indicadores de independência exigem conexão com o backend. Cadastros locais continuam disponíveis.
          </div>
        )}
        <form className="form-container goals-form" onSubmit={handleCreateGoal}>
          <h3>Nova meta</h3>
          <div className="form-group">
            <label>Nome</label>
            <input
              type="text"
              value={goalForm.name}
              onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Valor alvo</label>
              <input
                type="text"
                value={goalForm.target_amount}
                onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Data alvo</label>
              <input
                type="date"
                value={goalForm.target_date}
                onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Prioridade</label>
              <input
                type="text"
                value={goalForm.priority}
                onChange={(e) => setGoalForm({ ...goalForm, priority: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Plano relacionado</label>
              <select
                value={goalForm.plan_id ?? ''}
                onChange={(e) => setGoalForm({ ...goalForm, plan_id: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">Sem plano</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={goalSaving}>{goalSaving ? 'Salvando...' : 'Salvar meta'}</button>
          </div>
        </form>

        <div className="goals-list">
          <h3>Metas cadastradas</h3>
          {goals.map((goal) => (
            <div className="card" key={goal.id}>
              <strong>{goal.name}</strong>
              <span>Valor alvo: {goal.target_amount}</span>
              <span>Data alvo: {goal.target_date?.split('T')[0]}</span>
            </div>
          ))}
        </div>

        <form className="form-container fi-form" onSubmit={handleComputeFI}>
          <h3>Indicadores de independência financeira</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Plano</label>
              <select
                value={fiForm.plan_id}
                onChange={(e) => setFiForm({ ...fiForm, plan_id: Number(e.target.value) })}
              >
                <option value={0}>Selecione</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Gasto anual</label>
              <input
                type="text"
                value={fiForm.annual_expenses}
                onChange={(e) => setFiForm({ ...fiForm, annual_expenses: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Taxa de retirada</label>
            <input
              type="text"
              value={fiForm.withdrawal_rate}
              onChange={(e) => setFiForm({ ...fiForm, withdrawal_rate: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={!backendAvailable || fiComputing}>
              {fiComputing ? 'Calculando...' : 'Calcular indicadores'}
            </button>
          </div>
        </form>

        <FinancialIndependenceIndicators indicator={fiIndicator} />
      </div>
    </div>
  );
};

export default InvestmentGoalsPage;
